import React, { memo, useCallback, useMemo, useState } from "react"
import { Accordion, AccordionDetails, AccordionSummary, Grid, makeStyles, Typography } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight } from "@fortawesome/pro-light-svg-icons/faChevronRight"
import { faChevronDown } from "@fortawesome/pro-light-svg-icons/faChevronDown"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons/faInfoCircle"

import { toCurrency, toDinero } from "../../../../Helpers/CurrencyHelper"
import { useMoney } from "../../../Localization/useMoney"
import { StandardButton } from "../../../../Components/Button/StandardButton"
import { Localized } from "@fluent/react"
import { useFormikContext } from "formik"
import {
    IBasketExtraServicesFormValues,
    IDeliveryForm, IDeliveryFormExtraServiceItem,
    IExtraServiceAccordionProps
} from "../../../../Types/MeetingPlanning/deliveryForm"
import { dinero } from "dinero.js"

const useExtraServicesAccordionStyles = makeStyles((theme) => ({
    mb_1: {
        marginBottom: theme.spacing(1),
    },
    accordion: {
        width: "100%",
        boxShadow: 'unset',
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',

        "&:first-child": {
            borderTop: 0
        },

        "&::before": {
            content: 'none',
        },

        '& .MuiAccordionSummary-root': {
            padding: 0
        },

        '& .MuiAccordionSummary-content': {
            margin: 0
        }
    },
    accordionIcon: {
        width: '1.3rem',
        marginRight: '0.4rem'
    },
    accordionSummaryContent: {
        "&.Mui-expanded": {
            marginBottom: 0,
        }
    },
    infoIcon: {
        paddingTop: '0.12rem'
    },
    accordionHeaderIcon: {
        height: "1.1rem",
        width: "auto !important",
        marginRight: theme.spacing(1),
    },
}))

const ExtraServicesAccordion = ({ name, service, seats, selectedServices, setSelectedServices }: IExtraServiceAccordionProps) => {
    const classes = useExtraServicesAccordionStyles()

    const moneyFactory = useMoney()

    const [expanded, setExpanded] = useState(false)

    const fullPrice = useMemo(() => {
        const seatPrice = seats ? service.pricePerSeat.amount * Number(seats) : service.pricePerSeat.amount
        const min = service.mininumPrice.amount
        const max = service.maximumPrice.amount

        const clampedPrice = Math.min(Math.max(seatPrice, min), max)

        return {
            ...service.pricePerSeat,
            amount: clampedPrice,
            basePrice: service.pricePerSeat.amount
        }
    }, [service.pricePerSeat, service.mininumPrice, service.maximumPrice, seats])

    const isAdded = useMemo(() => {
        return selectedServices?.length ? selectedServices.some((s) => s.id === service.id) : false
    }, [selectedServices, service.id])

    const fullPriceFormatted = useMemo(() => {
        return moneyFactory.format(dinero({
            amount: fullPrice.amount,
            currency: toCurrency(service.pricePerSeat.currency),
        }))
    }, [moneyFactory, fullPrice.amount, service.pricePerSeat.currency])

    const pricePerSeatFormatted = useMemo(() => (
        moneyFactory.formatAsNumber(toDinero(service.pricePerSeat))
    ), [moneyFactory, service.pricePerSeat])

    const minFormatted = useMemo(() => (
        moneyFactory.formatAsNumber(toDinero(service.mininumPrice))
    ), [moneyFactory, service.mininumPrice])

    const maxFormatted = useMemo(() => (
        moneyFactory.formatAsNumber(toDinero(service.maximumPrice))
    ), [moneyFactory, service.maximumPrice])

    const handleExpansion = useCallback(() => {
        setExpanded((prevExpanded) => !prevExpanded)
    }, [])

    const addExtraService = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        const updated = [
            ...selectedServices,
            {
                id: service.id,
                name: service.name,
                amount: fullPrice.amount,
                basePrice: fullPrice.basePrice,
                mininumPrice: service.mininumPrice,
                maximumPrice: service.maximumPrice,
                currency: service.pricePerSeat.currency
            }
        ]
        setSelectedServices(updated)
    }, [selectedServices, service, fullPrice, setSelectedServices])

    const removeExtraService = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        const filteredServices = selectedServices.filter((s) => s.id !== service.id)
        setSelectedServices(filteredServices)
    }, [selectedServices, service, setSelectedServices])

    return (
        <Accordion
            expanded={expanded}
            onChange={handleExpansion}
            className={classes.accordion}
        >
            <AccordionSummary
                classes={{ content: classes.accordionSummaryContent }}
            >
                <Grid container direction="row" wrap="nowrap" alignItems="center" justifyContent="space-between">
                    <Grid container direction="row" wrap="nowrap" alignItems="center">
                        <Grid item className={classes.accordionIcon}>
                            <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} className={classes.accordionHeaderIcon} />
                        </Grid>
                        <Grid item>
                            <Typography variant="body1">{service.name}</Typography>
                            <Typography variant="body1" color="primary">
                                {fullPriceFormatted}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Localized id={isAdded ? 'delivery-form-delivery-extra-services-remove' : 'delivery-form-delivery-extra-services-add'}>
                            <StandardButton
                                variant={isAdded ? 'outlined' : 'contained'}
                                size="small"
                                color="primary"
                                onClick={isAdded ? removeExtraService : addExtraService}
                            >
                                {isAdded ? 'Fjern' : 'Tilføj'}
                            </StandardButton>
                        </Localized>
                    </Grid>
                </Grid>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container direction="column" wrap="nowrap" alignItems="flex-start">
                    <Grid container direction="row" wrap="nowrap" alignItems="flex-start" className={classes.mb_1}>
                        <Grid container direction="row" wrap="nowrap" alignItems="flex-start">
                            <Grid item className={classes.infoIcon}>
                                <FontAwesomeIcon icon={faInfoCircle} className={classes.accordionHeaderIcon} />
                            </Grid>
                            <Grid container direction="column" wrap="nowrap" alignItems="flex-start">
                                <Localized id="delivery-form-delivery-extra-services-per-person" vars={{
                                    pricePerSeat: pricePerSeatFormatted
                                }}>
                                    <Typography>
                                        {pricePerSeatFormatted} pr. kurvert
                                    </Typography>
                                </Localized>
                                <Localized id="delivery-form-delivery-extra-services-min-max" vars={{
                                    minimumPrice: minFormatted,
                                    maximumPrice: maxFormatted,
                                }}>
                                    <Typography>
                                        Min {minFormatted} | Maks {maxFormatted}
                                    </Typography>
                                </Localized>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Typography>{service.description}</Typography>
                </Grid>
            </AccordionDetails>
        </Accordion>
    )
}

export default memo(ExtraServicesAccordion)
